import * as Sentry from '@sentry/browser';
import { useEffect, useState } from 'react';
import { abortRefreshOnTimeout, refreshTokensSingleflight } from '../api/auth-client/refresh-singleton';
import {
  clearAuthenticationStore,
  getRefreshToken,
  isRefreshTokenValid,
  isValidAccessToken,
} from '../helpers/storage/storage';

/**
 * Lifecycle state of the on-load session-recovery attempt.
 *
 * - `Authenticated`: a valid access token is present; render the app.
 * - `Restoring`: access token expired but a valid refresh token exists; a
 *   silent refresh is in flight and the UI should show a loading state.
 * - `Unauthenticated`: no recoverable session (or a route/flow that must not be
 *   gated); render the children so the login/SSO/public route can proceed.
 */
enum SessionStatus {
  Authenticated = 'authenticated',
  Restoring = 'restoring',
  Unauthenticated = 'unauthenticated',
}

/**
 * Upper bound on how long the app may be blocked on a load-time refresh before
 * falling through to login. The refresh singleton itself allows a 6-minute
 * request timeout, which is acceptable for a background 401 retry but not for a
 * gate that withholds the entire UI — a half-open connection would otherwise
 * freeze the app (login included) for minutes.
 *
 * Note: on timeout the gate stops WAITING and routes to login; it does not abort
 * the underlying request. The in-flight refresh keeps running until it settles
 * or hits the singleton's own timeout, but its late result is dropped by
 * `abortRefreshOnTimeout` so it can neither resurrect nor clobber the session.
 */
const RESTORE_TIMEOUT_MS = 15000;

/**
 * Route prefixes that must render WITHOUT a session gate — the gate never shows
 * a loader or fires a silent (token-rotating) refresh on them, even if stale
 * cookies happen to be present.
 *
 * Covers the backend PDF renderer's artifact-export route AND the public
 * onboarding / auth-recovery routes. A user with a lingering valid refresh
 * cookie who opens an activation or password-reset link must see that page
 * instantly (not a loader), and such a page must not silently rotate the stored
 * refresh token. A genuinely-needed restore still happens lazily the moment the
 * user navigates to a protected `/application/*` route.
 */
const AUTHLESS_PATH_PREFIXES = [
  '/_artifact-export',
  '/sign-up',
  '/activate',
  '/reset-password',
  '/password/reset/confirm',
  '/create-password-success',
];

/**
 * Reports whether the current pathname is one that must bypass session gating.
 *
 * @returns {boolean} True if the path is an auth-less route
 */
function isAuthlessPath(): boolean {
  return AUTHLESS_PATH_PREFIXES.some((prefix) => window.location.pathname.startsWith(prefix));
}

/**
 * Detects whether the current URL is an SSO callback the login page must own.
 *
 * The login page's `useAuthParams` handles two SSO callback shapes: SUCCESS
 * carries both `access` and `refresh` query params; FAILURE carries an `error`
 * param. In either case the gate must defer (render the app so `useAuthParams`
 * runs) rather than silently refreshing a stale cookie — which would race the
 * SSO token storage on success, or swallow the error and silently relogin the
 * old session on failure.
 *
 * Reads `window.location.search` directly (rather than react-router's
 * `useSearchParams`) so the check works above the router and reflects a stable
 * first-render snapshot — mirroring how the login page's own hooks detect SSO.
 *
 * @returns {boolean} True if the URL carries SSO success tokens or an SSO error
 */
function isSsoCallbackInUrl(): boolean {
  // The SSO callback always lands on the root path, so restrict the check to it.
  // Otherwise an unrelated `?error=` (or stray access/refresh params) on a
  // protected route would bypass restoration and force a spurious logout — the
  // exact failure this gate exists to prevent.
  if (window.location.pathname !== '/') {
    return false;
  }
  const params = new URLSearchParams(window.location.search);
  return !!((params.get('access') && params.get('refresh')) || params.get('error'));
}

/**
 * Computes the synchronous starting status before any async refresh runs, so
 * the first render already reflects the fast paths (valid access / no session)
 * and only the genuinely-recoverable case pays for a loading state.
 *
 * Order matters: a valid access token wins outright (so a logged-in user is
 * never bounced), auth-less routes and SSO callbacks bypass gating, and only
 * then is an expired-but-recoverable session marked `Restoring`.
 *
 * @returns {SessionStatus} The initial status for the current cookies/URL
 */
function computeInitialStatus(): SessionStatus {
  if (isAuthlessPath()) {
    return SessionStatus.Unauthenticated;
  }
  if (isValidAccessToken()) {
    return SessionStatus.Authenticated;
  }
  if (isSsoCallbackInUrl()) {
    return SessionStatus.Unauthenticated;
  }
  if (isRefreshTokenValid()) {
    return SessionStatus.Restoring;
  }
  return SessionStatus.Unauthenticated;
}

/**
 * Resolves the app's auth state on load, silently refreshing an expired access
 * token when a valid refresh token exists instead of bouncing the user to the
 * login screen.
 *
 * Responsibilities:
 * 1. Seed the status synchronously so valid/no-session loads never flash a loader
 * 2. When `Restoring`, run a single deduplicated refresh via the singleton,
 *    bounded by `RESTORE_TIMEOUT_MS` so a hung backend cannot freeze the app
 * 3. Flip to `Authenticated` on success; on failure or timeout clear the
 *    (now-unusable) auth store and flip to `Unauthenticated`, ignoring any
 *    resolution that lands after unmount. On timeout it also tells the refresh
 *    singleton to drop a late-arriving result (`abortRefreshOnTimeout`) so the
 *    abandoned session cannot be resurrected — without suppressing a sibling
 *    tab's concurrent, winning refresh
 * 4. Emit a Sentry signal when the gate gives up so a deploy-day refresh outage
 *    is observable (a breadcrumb for an ordinary failed refresh; a warning for a
 *    timeout, which means the auth backend is degraded)
 *
 * @returns {SessionStatus} The current session-resolution status
 */
function useSessionRestore(): SessionStatus {
  const [status, setStatus] = useState<SessionStatus>(computeInitialStatus);

  useEffect(() => {
    if (status !== SessionStatus.Restoring) {
      return;
    }

    const refresh = getRefreshToken();
    if (!refresh) {
      setStatus(SessionStatus.Unauthenticated);
      return;
    }

    let settled = false;

    // Clearing the store on failure/timeout prevents the ProtectedRoute
    // instance of this hook from re-attempting the same clock-valid-but-doomed
    // refresh (the singleton is blacklist-blind), and matches how the login
    // page's refresh hook and the axios interceptor treat a failed refresh.
    const failToLogin = (): void => {
      if (settled) {
        return;
      }
      settled = true;
      clearAuthenticationStore();
      setStatus(SessionStatus.Unauthenticated);
    };

    // On TIMEOUT (distinct from the failure path) the refresh POST may still be
    // in flight and resolve late. Signal the singleton to drop that late result
    // so it cannot resurrect the session we are about to clear — scoped to THIS
    // tab, so a sibling tab's concurrent winning refresh is unaffected. Surface
    // a warning: a >15s refresh means the auth backend is degraded, exactly the
    // signal on-call wants during/after a deploy.
    const onTimeout = (): void => {
      abortRefreshOnTimeout();
      Sentry.captureMessage('Session restore timed out before the refresh resolved', 'warning');
      failToLogin();
    };

    const timeoutId = window.setTimeout(onTimeout, RESTORE_TIMEOUT_MS);

    refreshTokensSingleflight(refresh)
      .then(() => {
        if (settled) {
          return;
        }
        settled = true;
        window.clearTimeout(timeoutId);
        setStatus(SessionStatus.Authenticated);
      })
      .catch(() => {
        window.clearTimeout(timeoutId);
        // An ordinary expired/blacklisted refresh is expected (the user simply
        // needs to sign in again), so record a breadcrumb rather than an error
        // event — but it still makes a spike of load-time refresh failures
        // visible in Sentry when correlated with the eventual login.
        Sentry.addBreadcrumb({
          category: 'auth',
          level: 'info',
          message: 'Session restore refresh failed; routing to the login page',
        });
        failToLogin();
      });

    return () => {
      // Block any post-unmount state update; do NOT clear auth on unmount.
      settled = true;
      window.clearTimeout(timeoutId);
    };
  }, [status]);

  return status;
}

export { SessionStatus, useSessionRestore };
